import * as c from 'common/c'
import { get, findIndex, cloneDeep } from 'es-toolkit/compat'
import { type FarmOptionModel } from 'state/farm'

declare module 'state/client.js' {
  export type IntegrationData = {
    id: string
    farms: string
    fields: string
    self: string
    archived: boolean
  }

  export type ClientModel = {
    id: number
    name: string
    streetAddress?: string
    postalCode?: string
    email?: string
    farms: FarmOptionModel[]
    integrationIds: { [key: string]: IntegrationData }
  }
  export type Client = {
    collection: ClientModel[]
    singleton: ClientModel | undefined
    clientFieldFeaturesFetched: { [key: number | string]: number[] }
  }
}

const initialState = {
  collection: [],
  singleton: undefined,
  clientFieldFeaturesFetched: {},
}

export default (state = initialState, action) => {
  switch (action.type) {
    case c.CLIENT_CREATED: {
      const obj = { ...action.payload, farms: [] }
      //@ts-expect-error
      return update(state, {
        collection: { $unshift: [obj] },
        singleton: { $set: obj },
      })
    }

    case c.CLIENTS_FETCHED: {
      //@ts-expect-error
      return update(state, {
        collection: { $set: action.payload },
        singleton: { $set: action.payload[0] },
      })
    }

    case c.CLIENT_EDITED: {
      const index = findIndex(
        get(state, 'collection', []),
        (client: ClientModel) => client.id === action.payload.id,
      )
      if (index > -1) {
        //@ts-expect-error
        return update(state, {
          collection: { $splice: [[index, 1, action.payload]] },
        })
      } else return
    }

    case c.CLIENT_DELETED: {
      const index = findIndex(
        get(state, 'collection', []),
        (client: ClientModel) => client.id === action.payload,
      )
      if (index > -1) {
        //@ts-expect-error
        return update(state, {
          collection: { $splice: [[index, 1]] },
        })
      } else return
    }

    case c.CLIENTS_DELETED: {
      const ids = action.payload
      const currentClients: ClientModel[] = cloneDeep(state.collection)

      const final: ClientModel[] = currentClients.filter(
        (p) => !ids.includes(p.id),
      )
      //@ts-expect-error
      return update(state, { collection: { $set: final } })
    }

    case c.FARM_REFERENCE_ADDED: {
      const { id, clientId, name } = action.payload

      const index = findIndex(
        get(state, 'collection', []),
        (client: ClientModel) => client.id === clientId,
      )

      if (index > -1) {
        //@ts-expect-error
        return update(state, {
          collection: {
            [index]: {
              farms: {
                $push: [
                  {
                    id,
                    name,
                  },
                ],
              },
            },
          },
        })
      } else return
    }

    case c.FARM_REFERENCE_UPDATED: {
      const { data } = action.payload

      if (!data.clientChanged) {
        const index = findIndex(
          get(state, 'collection', []),
          (client: ClientModel) => client.id === data.clientId,
        )

        if (index > -1) {
          const clientObj = state.collection.at(index)
          //@ts-expect-error
          const farmIndex = findIndex(clientObj?.farms, ['id', data['id']])
          //@ts-expect-error
          return update(state, {
            collection: {
              [index]: {
                farms: {
                  $splice: [[farmIndex, 1, { id: data.id, name: data.name }]],
                },
              },
            },
          })
        } else return
      } else {
        const newClientIndex = findIndex(
          get(state, 'collection', []),
          (client: ClientModel) => client.id === data['clientId'],
        )

        const oldClientIndex = findIndex(
          get(state, 'collection', []),
          (client: ClientModel) => client.id === data['oldClientId'],
        )

        //Add the farm to the new selected client and Delete the farm from the old selected client
        if (newClientIndex > -1 && oldClientIndex > -1) {
          const clientObj = state.collection.at(oldClientIndex)
          //@ts-expect-error
          const farmIndex = findIndex(clientObj?.farms, ['id', data['id']])

          //@ts-expect-error
          const newData = update(state, {
            collection: {
              [newClientIndex]: {
                farms: {
                  $push: [
                    {
                      id: data['id'],
                      name: data['name'],
                    },
                  ],
                },
              },
              [oldClientIndex]: {
                farms: {
                  $splice: [[farmIndex, 1]],
                },
              },
            },
          })
          return newData
        } else return
      }
    }

    case c.FARM_REFERENCE_DELETED: {
      const { id, clientId } = action.payload

      const index = findIndex(
        get(state, 'collection', []),
        (client: ClientModel) => client.id === clientId,
      )

      if (index > -1) {
        const clientObj = state.collection.at(index)
        //@ts-expect-error
        const farmIndex = findIndex(clientObj?.farms, ['id', id])
        //clientObj?.farms?.splice(farmIndex, 1)
        if (farmIndex > -1) {
          //@ts-expect-error
          return update(state, {
            collection: {
              [index]: { farms: { $splice: [[farmIndex, 1]] } },
            },
          })
        } else return state
      }
    }

    case c.FARM_REFERENCES_UPDATED: {
      const { selectedIds, selectedClient, selectedFarms } = action.payload
      const clients: ClientModel[] = cloneDeep(state.collection)

      const newClientIndex = findIndex(
        get(state, 'collection', []),
        (client: ClientModel) => client.id === selectedClient,
      )

      selectedFarms.map((item) => {
        const oldClientIndex = findIndex(
          get(state, 'collection', []),
          (client: ClientModel) => client.id === item.clientId,
        )
        //For adding the newly selected farms to the client
        if (newClientIndex > -1 && oldClientIndex > -1) {
          item.clientId = selectedClient
          clients[newClientIndex]['farms'].push(item)

          const farmIndex = findIndex(clients[oldClientIndex]['farms'], [
            'id',
            item['id'],
          ])
          clients[oldClientIndex]['farms'].splice(farmIndex, 1)
        }
      })
      //@ts-expect-error
      return update(state, { collection: { $set: clients } })
    }

    case c.CLIENT_SELECTED: {
      //@ts-expect-error
      return update(state, { singleton: { $set: action.payload } })
    }

    case c.FEATURES_FOR_CLIENT_FETCHED: {
      const clientsWithFeatures = cloneDeep(state.clientFieldFeaturesFetched)
      const { clientId, bbox } = action.payload
      if (clientsWithFeatures[clientId] === undefined) {
        clientsWithFeatures[clientId] = bbox
        //@ts-expect-error
        return update(state, {
          clientFieldFeaturesFetched: { $set: clientsWithFeatures },
        })
      }
      return state
    }

    default:
      return state
  }
}
